import { useCallback, useEffect, useRef, useState } from "react";
import { PASSWORD_CHANGED_MESSAGE, getCredentials, getOwnerEmail, isOptInDeclined, normalizeEmail, readAvailability, redirectToAppHome, setCredentials, setOptInDeclined, setOwnerEmail, setSkipAutoPrompt, shouldSkipAutoPrompt, verifyIdentity, wipeSavedLogin, type BiometricAvailability } from "@/lib/biometric-login";
import { requestLogin } from "./use-login";
const EMPTY_AVAILABILITY: BiometricAvailability = {
  usable: false,
  label: "Biometrics",
  saved: false,
  ownerEmail: "",
  occupied: false
};
const BIOMETRIC_FAILED_MESSAGE = "Biometric sign-in failed. Sign in with email and password to continue.";
const NETWORK_FAILED_MESSAGE = "Unable to login. Check that the API server is running.";
const OPT_IN_TIMEOUT_MS = 8000;
type OptInRequest = {
  email: string;
  password: string;
  label: string;
};
type UseBiometricLoginOptions = {
  enabled: boolean;
  onError: (message: string, email?: string) => void;
  onPrefillEmail: (email: string) => void;
};
export function useBiometricLogin({
  enabled,
  onError,
  onPrefillEmail
}: UseBiometricLoginOptions) {
  const [availability, setAvailability] = useState<BiometricAvailability>(EMPTY_AVAILABILITY);
  const [optIn, setOptIn] = useState<OptInRequest | null>(null);
  const [optInPending, setOptInPending] = useState(false);
  const busyRef = useRef(false);
  const onErrorRef = useRef(onError);
  const onPrefillRef = useRef(onPrefillEmail);
  const finishOptInRef = useRef<(() => void) | null>(null);
  onErrorRef.current = onError;
  onPrefillRef.current = onPrefillEmail;
  const goHome = useCallback(() => {
    try {
      localStorage.setItem("login-event", `login${Math.random()}`);
    } catch {
      // Parallel-tab reload is best-effort.
    }
    redirectToAppHome();
  }, []);
  const signIn = useCallback(async () => {
    if (!enabled || busyRef.current) {
      return;
    }
    busyRef.current = true;
    try {
      const identity = await verifyIdentity();
      if (!identity.ok) {
        if (!identity.cancelled) {
          onErrorRef.current(BIOMETRIC_FAILED_MESSAGE, getOwnerEmail() || undefined);
        }
        return;
      }
      const creds = await getCredentials();
      if (!creds?.username || !creds.password) {
        onErrorRef.current(BIOMETRIC_FAILED_MESSAGE, getOwnerEmail() || undefined);
        return;
      }
      let result;
      try {
        result = await requestLogin({
          username: creds.username,
          password: creds.password
        });
      } catch {
        onErrorRef.current(NETWORK_FAILED_MESSAGE, creds.username);
        return;
      }
      if (!result.ok) {
        if (result.statusCode === 401) {
          const cleared = await wipeSavedLogin(false);
          if (cleared) {
            setAvailability(previous => ({
              ...previous,
              saved: false,
              ownerEmail: "",
              occupied: false
            }));
          }
          onErrorRef.current(PASSWORD_CHANGED_MESSAGE, creds.username);
          return;
        }
        onErrorRef.current(BIOMETRIC_FAILED_MESSAGE, creds.username);
        return;
      }
      if (!result.data.accessToken) {
        onErrorRef.current(BIOMETRIC_FAILED_MESSAGE, creds.username);
        return;
      }
      setOwnerEmail(creds.username);
      goHome();
    } finally {
      busyRef.current = false;
    }
  }, [enabled, goHome]);
  useEffect(() => {
    if (!enabled) {
      setAvailability(EMPTY_AVAILABILITY);
      return;
    }
    let cancelled = false;
    void readAvailability(true).then(state => {
      if (cancelled) {
        return;
      }
      setAvailability(state);
      if (state.ownerEmail) {
        onPrefillRef.current(state.ownerEmail);
      }
      if (state.usable && state.saved && !shouldSkipAutoPrompt()) {
        void signIn();
      }
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, signIn]);
  const usePasswordInstead = useCallback(() => {
    setSkipAutoPrompt();
  }, []);
  const finishOptIn = useCallback(() => {
    setOptIn(null);
    setOptInPending(false);
    const resolve = finishOptInRef.current;
    finishOptInRef.current = null;
    resolve?.();
  }, []);
  const offerOptIn = useCallback(async (email: string, password: string) => {
    if (!enabled || !email || !password) {
      return;
    }
    let state_0: BiometricAvailability;
    try {
      state_0 = await readAvailability(true);
    } catch {
      return;
    }
    if (!state_0.usable || state_0.saved || state_0.occupied || isOptInDeclined()) {
      return;
    }
    if (state_0.ownerEmail && state_0.ownerEmail !== normalizeEmail(email)) {
      return;
    }
    await new Promise<void>(resolve_0 => {
      finishOptInRef.current = resolve_0;
      setOptIn({
        email,
        password,
        label: state_0.label
      });
    });
  }, [enabled]);
  const enableOptIn = useCallback(async () => {
    if (!optIn || optInPending) {
      return;
    }
    setOptInPending(true);
    await new Promise<void>(resolve_1 => {
      const timer = window.setTimeout(resolve_1, OPT_IN_TIMEOUT_MS);
      void setCredentials(optIn.email, optIn.password).then(() => {
        window.clearTimeout(timer);
        resolve_1();
      }, () => {
        window.clearTimeout(timer);
        resolve_1();
      });
    });
    finishOptIn();
  }, [finishOptIn, optIn, optInPending]);
  const declineOptIn = useCallback(() => {
    if (optInPending) {
      return;
    }
    setOptInDeclined();
    finishOptIn();
  }, [finishOptIn, optInPending]);
  return {
    showSignIn: enabled && availability.usable && availability.saved,
    label: availability.label,
    ownerEmail: availability.ownerEmail,
    signIn,
    usePasswordInstead,
    offerOptIn,
    optIn: {
      open: optIn !== null,
      label: optIn?.label ?? availability.label,
      pending: optInPending,
      onEnable: () => {
        void enableOptIn();
      },
      onDecline: declineOptIn
    }
  };
}